import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import TextField from '@shared/ui/atoms/TextField';
import { Row } from '@shared/ui/atoms/StructuralLayout';
import Colours from '@shared/ui/atoms/Colours';
import { IsPublicApiKeyEnabled } from '@shared/hooks/IsPublicApiKeyEnabled';
import { ConfigurationSection } from '../../StyleComponents';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import {
  ApiKeyDetails,
  ApiKeysResponse,
  generateScimApiKey,
  getApiKeys,
  regeneratePublicApiKey,
  revokePublicApiKey,
  revokeScimApiKey,
  updatePublicApiKeyLabel,
  updateScimApiKeyLabel,
} from '../../../services/CompanyService';

export function ApiKeysPanel() {
  const { t } = useTranslation();
  const publicApiKeyEnabled = IsPublicApiKeyEnabled();
  const [keys, setKeys] = useState<ApiKeysResponse | undefined>(undefined);

  useEffect(() => {
    getApiKeys().then(setKeys);
  }, []);

  return (
    <>
      <HeadlineSmall style={{ marginBottom: 16 }}>{t('company-settings-page.api-keys-tab')}</HeadlineSmall>

      {!keys && <SimpleSpinner />}

      {keys && (
        <>
          <ScimApiKeyList
            values={keys.scimApiKeys}
            onGenerate={async (label) => {
              const details = await generateScimApiKey(label);
              setKeys(prev => ({
                ...(prev as ApiKeysResponse),
                scimApiKeys: [...((prev as ApiKeysResponse).scimApiKeys), details],
              }));
            }}
            onUpdateLabel={async (apiKey: string, label: string | null) => {
              const details = await updateScimApiKeyLabel(apiKey, label);
              setKeys(prev => ({
                ...(prev as ApiKeysResponse),
                scimApiKeys: (prev as ApiKeysResponse).scimApiKeys.map(k => k.apiKey === details.apiKey ? details : k),
              }));
            }}
            onRevoke={async (apiKey: string) => {
              await revokeScimApiKey(apiKey);
              setKeys(prev => ({
                ...(prev as ApiKeysResponse),
                scimApiKeys: (prev as ApiKeysResponse).scimApiKeys.filter(k => k.apiKey !== apiKey),
              }));
            }}
          />
          {publicApiKeyEnabled && (
            <SingleApiKeyRow
              titleKey={'company-settings-page.api-keys.public-api.title'}
              descriptionKey={'company-settings-page.api-keys.public-api.description'}
              value={keys.publicApiKey}
              onGenerate={async (label) => {
                const details = await regeneratePublicApiKey(label);
                setKeys(prev => ({ ...(prev as ApiKeysResponse), publicApiKey: details }));
              }}
              onUpdateLabel={async (label) => {
                const details = await updatePublicApiKeyLabel(label);
                setKeys(prev => ({ ...(prev as ApiKeysResponse), publicApiKey: details }));
              }}
              onRevoke={async () => {
                await revokePublicApiKey();
                setKeys(prev => ({ ...(prev as ApiKeysResponse), publicApiKey: null }));
              }}
            />
          )}
        </>
      )}
    </>
  );
}

interface SingleApiKeyRowProps {
  titleKey: string;
  descriptionKey: string;
  value: ApiKeyDetails | null;
  onGenerate: (label: string | null) => Promise<void>;
  // Omit to disable labelling entirely.
  onUpdateLabel?: (label: string | null) => Promise<void>;
  onRevoke: () => Promise<void>;
  placeholderKey?: string;
}

function SingleApiKeyRow({ titleKey, descriptionKey, value, onGenerate, onUpdateLabel, onRevoke, placeholderKey = 'company-settings-page.api-keys.label-placeholder' }: SingleApiKeyRowProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [labelInput, setLabelInput] = useState('');
  const supportsLabel = !!onUpdateLabel;

  const handleGenerate = async () => {
    if (value && !window.confirm(t('company-settings-page.api-keys.confirm-regenerate'))) return;
    setBusy(true);
    try {
      const trimmed = labelInput.trim();
      await onGenerate(trimmed === '' ? null : trimmed);
      setLabelInput('');
    } finally { setBusy(false); }
  };

  const handleRevoke = async () => {
    if (!window.confirm(t('company-settings-page.api-keys.confirm-revoke'))) return;
    setBusy(true);
    try { await onRevoke(); } finally { setBusy(false); }
  };

  return (
    <ConfigurationSection>
      <BodyLarge colour={Colours.blue} style={{ marginBottom: 4 }}>{t(titleKey)}</BodyLarge>
      <BodyRegular>{t(descriptionKey)}</BodyRegular>

      {value ? (
        <>
          <KeyValueRow value={value} onUpdateLabel={onUpdateLabel} />
          <ButtonRow>
            {supportsLabel && (
              <LabelInput
                type={'text'}
                value={labelInput}
                maxLength={80}
                placeholder={t(placeholderKey) ?? ''}
                onChange={(e) => setLabelInput(e.target.value)}
                disabled={busy}
              />
            )}
            <PrimaryButton
              text={'company-settings-page.api-keys.regenerate'}
              size={ButtonSize.Small}
              click={handleGenerate}
              disabled={busy}
            />
            <OutlineButton
              text={'company-settings-page.api-keys.revoke'}
              size={ButtonSize.Small}
              click={handleRevoke}
              disabled={busy}
            />
          </ButtonRow>
        </>
      ) : (
        <>
          <BodyRegular style={{ marginTop: 12, color: Colours.darkGrey }}>
            {t('company-settings-page.api-keys.no-key')}
          </BodyRegular>
          <ButtonRow>
            {supportsLabel && (
              <LabelInput
                type={'text'}
                value={labelInput}
                maxLength={80}
                placeholder={t(placeholderKey) ?? ''}
                onChange={(e) => setLabelInput(e.target.value)}
                disabled={busy}
              />
            )}
            <PrimaryButton
              text={'company-settings-page.api-keys.generate'}
              size={ButtonSize.Small}
              click={handleGenerate}
              disabled={busy}
            />
          </ButtonRow>
        </>
      )}
    </ConfigurationSection>
  );
}

interface ScimApiKeyListProps {
  values: ApiKeyDetails[];
  onGenerate: (label: string | null) => Promise<void>;
  onUpdateLabel: (apiKey: string, label: string | null) => Promise<void>;
  onRevoke: (apiKey: string) => Promise<void>;
}

function ScimApiKeyList({ values, onGenerate, onUpdateLabel, onRevoke }: ScimApiKeyListProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [labelInput, setLabelInput] = useState('');

  const handleGenerate = async () => {
    setBusy(true);
    try {
      const trimmed = labelInput.trim();
      await onGenerate(trimmed === '' ? null : trimmed);
      setLabelInput('');
    } finally { setBusy(false); }
  };

  const handleRevoke = async (apiKey: string) => {
    if (!window.confirm(t('company-settings-page.api-keys.confirm-revoke'))) return;
    setBusy(true);
    try { await onRevoke(apiKey); } finally { setBusy(false); }
  };

  return (
    <ConfigurationSection>
      <BodyLarge colour={Colours.blue} style={{ marginBottom: 4 }}>{t('company-settings-page.api-keys.scim.title')}</BodyLarge>
      <BodyRegular>{t('company-settings-page.api-keys.scim.description')}</BodyRegular>

      {values.length === 0 ? (
        <BodyRegular style={{ marginTop: 12, color: Colours.darkGrey }}>
          {t('company-settings-page.api-keys.no-key')}
        </BodyRegular>
      ) : (
        values.map(details => (
          <KeyValueRow
            key={details.apiKey}
            value={details}
            onUpdateLabel={(label) => onUpdateLabel(details.apiKey, label)}
            onRevoke={() => handleRevoke(details.apiKey)}
            disabled={busy}
          />
        ))
      )}

      <AddRow>
        <TextField
          value={labelInput}
          onChange={setLabelInput}
          placeholder={'company-settings-page.api-keys.label-placeholder'}
          disabled={busy}
          style={{ flex: 1, marginBottom: 0, marginRight: 12 }}
        />
        <PrimaryButton
          text={'company-settings-page.api-keys.generate-additional'}
          size={ButtonSize.Small}
          click={handleGenerate}
          disabled={busy}
        />
      </AddRow>
    </ConfigurationSection>
  );
}

interface KeyValueRowProps {
  value: ApiKeyDetails;
  onUpdateLabel?: (label: string | null) => Promise<void>;
  onRevoke?: () => Promise<void>;
  disabled?: boolean;
}

function KeyValueRow({ value, onUpdateLabel, onRevoke, disabled }: KeyValueRowProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const startEdit = () => {
    setEditValue(value.label ?? '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!onUpdateLabel) return;
    setSaving(true);
    try {
      const trimmed = editValue.trim();
      await onUpdateLabel(trimmed === '' ? null : trimmed);
      setEditing(false);
      setEditValue('');
    } finally { setSaving(false); }
  };

  const rowDisabled = disabled || saving;

  return (
    <KeyValueWrapper>
      {editing ? (
        <LabelEditRow>
          <LabelInput
            type={'text'}
            value={editValue}
            maxLength={80}
            placeholder={t('company-settings-page.api-keys.label-placeholder') ?? ''}
            onChange={(e) => setEditValue(e.target.value)}
            disabled={saving}
            autoFocus
          />
          <PrimaryButton
            text={'company-settings-page.api-keys.save-label'}
            size={ButtonSize.Small}
            click={saveEdit}
            disabled={saving}
          />
          <OutlineButton
            text={'company-settings-page.api-keys.cancel'}
            size={ButtonSize.Small}
            click={cancelEdit}
            disabled={saving}
          />
        </LabelEditRow>
      ) : (
        <LabelDisplayRow>
          {value.label
            ? <BodyRegular weight={600}>{value.label}</BodyRegular>
            : <BodySmall style={{ color: Colours.darkGrey, fontStyle: 'italic' }}>
                {t('company-settings-page.api-keys.unlabeled')}
              </BodySmall>}
          {onUpdateLabel && (
            <BorderlessLabelButton type={'button'} onClick={startEdit} disabled={rowDisabled}>
              {t('company-settings-page.api-keys.edit-label')}
            </BorderlessLabelButton>
          )}
        </LabelDisplayRow>
      )}
      <KeyValueContainer>
        <KeyValue>{value.apiKey}</KeyValue>
        <KeyValueActions>
          <OutlineButton
            text={copied ? 'company-settings-page.api-keys.copied' : 'company-settings-page.api-keys.copy'}
            size={ButtonSize.Small}
            click={handleCopy}
            disabled={rowDisabled}
          />
          {onRevoke && (
            <OutlineButton
              text={'company-settings-page.api-keys.revoke'}
              size={ButtonSize.Small}
              click={onRevoke}
              disabled={rowDisabled}
            />
          )}
        </KeyValueActions>
      </KeyValueContainer>
    </KeyValueWrapper>
  );
}

const KeyValueWrapper = styled.div`
  margin-top: 12px;
`;

const KeyValueContainer = styled.div`
  margin-top: 4px;
  display: flex;
  gap: 8px;
  align-items: stretch;
  flex-wrap: wrap;
`;

const KeyValue = styled.div`
  flex: 1;
  min-width: 240px;
  padding: 8px 12px;
  background-color: ${Colours.veryLightGrey};
  border: 1px solid ${Colours.mildGrey};
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  word-break: break-all;
`;

const KeyValueActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

// Mirrors the "Automatic Office Check-in" MAC address add row (UserMacAddressSection).
const AddRow = styled(Row)`
  align-items: flex-end;
  margin-top: 12px;
  margin-bottom: 16px;
`;

const LabelDisplayRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const LabelEditRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const BorderlessLabelButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  color: ${Colours.blue};
  cursor: pointer;
  &:disabled {
    cursor: default;
    color: ${Colours.mildGrey};
  }
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;

const LabelInput = styled.input`
  height: 32px;
  padding: 0 10px;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
  &:disabled {
    background-color: ${Colours.veryLightGrey};
  }
`;
